import React from 'react';
import { MdMenu, MdClose, MdKeyboard } from 'react-icons/md';

export const MobileHeader = ({ 
  mobileMenuOpen, 
  setMobileMenuOpen, 
  setShowKeyboardHelp,
  activeTab,
  setActiveTab,
  showIntonationVisualization,
  setShowIntonationVisualization,
  visualizationView,
  setVisualizationView,
  translation,
  setTranslation
}) => {
  return (
    <>
      <div className="lg:hidden flex items-center justify-between p-4 bg-white/80 backdrop-blur-sm border-b border-white/30 sticky top-0 z-40">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="hamburger-button p-2 hover:bg-white/60 rounded-xl transition-all duration-200 border border-white/40 shadow-sm"
        >
          {mobileMenuOpen ? <MdClose className="text-gray-600 text-xl" /> : <MdMenu className="text-gray-600 text-xl" />}
        </button>
        
        <h1 className="font-bold text-gray-800 text-lg flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white text-sm">
            🎵
          </div>
          Language Practice
        </h1>
        
        <button
          onClick={() => setShowKeyboardHelp(true)}
          className="keyboard-help-button p-2 hover:bg-white/60 rounded-xl transition-all duration-200 border border-white/40 shadow-sm"
        >
          <MdKeyboard className="text-gray-600 text-xl" />
        </button>
      </div>

      {/* MOBILE MENU OVERLAY */}
      {mobileMenuOpen && (
        <div className="mobile-menu fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute top-0 left-0 w-80 h-full bg-white/95 backdrop-blur-sm border-r border-white/30 shadow-xl animate-in slide-in-from-left-1 duration-300">
            <div className="p-6 h-full overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-bold text-gray-800 text-xl">Menu</h2>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  <MdClose className="text-gray-500 text-xl" />
                </button>
              </div>

              {/* Practice Mode Section */}
              <div className="bg-white/60 rounded-2xl p-4 border border-gray-200 mb-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white text-sm">
                    🎵
                  </div>
                  Practice Mode
                </h3>
                
                <div className="flex gap-3">
                  <button
                    className={`flex-1 text-center py-3 px-4 rounded-xl transition-all duration-300 font-medium text-sm ${
                      activeTab === 'sentence'
                      ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/25"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => {
                      setActiveTab('sentence');
                      setMobileMenuOpen(false);
                    }}
                  >
                    By Sentence
                  </button>
                  <button
                    className={`flex-1 text-center py-3 px-4 rounded-xl transition-all duration-300 font-medium text-sm ${
                      activeTab === 'full'
                      ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/25"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => {
                      setActiveTab('full');
                      setMobileMenuOpen(false);
                    }}
                  >
                    Full Audio
                  </button>
                </div>
              </div>

              {/* Visualization Controls */}
              <div className="bg-white/60 rounded-2xl p-4 border border-gray-200 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white text-sm">
                      📊
                    </div>
                    Visualization
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowIntonationVisualization(!showIntonationVisualization)}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 ${
                        showIntonationVisualization 
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                          : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-all duration-300 ${
                          showIntonationVisualization ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {showIntonationVisualization && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-700 text-sm">View Type</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'pitch', icon: '📈', label: 'Pitch' },
                        { key: 'intensity', icon: '🔊', label: 'Intensity' },
                        { key: 'music', icon: '🎵', label: 'Music' },
                        { key: 'colors', icon: '🎨', label: 'Colors' },
                        { key: 'combined', icon: '🌀', label: 'Combined', colSpan: true }
                      ].map(({ key, icon, label, colSpan }) => (
                        <button
                          key={key}
                          onClick={() => {
                            setVisualizationView(key);
                            setMobileMenuOpen(false);
                          }}
                          className={`p-3 rounded-xl transition-all duration-300 flex items-center gap-2 text-sm ${
                            visualizationView === key 
                              ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg" 
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          } ${colSpan ? 'col-span-2 justify-center' : ''}`}
                        >
                          <span>{icon}</span>
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};